import type { AttendeeFreeBusy } from './day-timeline-layout'
import { instantToZonedWallClock } from './datetime-wire'

/**
 * Re-project attendee busy intervals (absolute-instant ISO strings from the
 * provider) into the draft's WALL-CLOCK frame — the same reframe the own-events
 * overlay applies to `dayEvents`.
 *
 * Why: the day timeline edits in wall-clock. A timed draft's `start`/`end` are
 * naive `Date`s whose fields are the event-zone civil time (`parseTimedWallClock`
 * ignores the RFC3339 offset), so `start.getTime()` is the wall-clock read in the
 * BROWSER zone, not the true instant. The proposed band, own events and conflict
 * span are all in that frame. Provider busy intervals, by contrast, are true
 * instants — so to compare/draw them consistently we must express them in the
 * same wall-clock frame, i.e. read each instant's civil time in the event zone.
 * Without this, conflict detection and the violet bands are offset-wrong for any
 * event whose `timeZone` differs from the browser's.
 *
 * No-op when there's no differing zone (the busy instants already share the
 * browser-local frame the picker uses). Malformed intervals pass through.
 */
export function reframeAttendeesToZone(
  attendees: Array<AttendeeFreeBusy>,
  timeZone: string | undefined,
): Array<AttendeeFreeBusy> {
  if (!timeZone) return attendees
  return attendees.map((a) => ({
    ...a,
    busy: a.busy.map((b) => {
      const start = new Date(b.start)
      const end = new Date(b.end)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return b
      return {
        start: instantToZonedWallClock(start, timeZone).toISOString(),
        end: instantToZonedWallClock(end, timeZone).toISOString(),
      }
    }),
  }))
}
